import { FILE_MAX_SIZE_BYTES, FileSharePermission } from '@atlas/types'
import { z } from 'zod'
import { classifyRowSchema } from '../classification/classification.schema.js'

/**
 * Schema for requesting a file upload URL (HTTP POST /files/upload-url body).
 */
export const fileUploadRequestSchema = z.object({
	fileName: z.string().min(1),
	contentType: z.string().min(1),
	checksum: z.string().optional(),
	entityType: z.string().optional(),
	entityId: z.uuid().optional(),
	isPublic: z.boolean().default(false),
	description: z.string().optional(),
	idempotencyKey: z.uuid().optional(),
})

/**
 * Schema for creating a File without presigning (HTTP POST /files body).
 *
 * `idempotencyKey` is optional and, when present, must be a UUID: a caller that
 * omits it sends exactly the request it always did and always gets a fresh
 * File. When it is present the create is a replay-safe operation — the same key
 * resolves to the same File, and the same key presented with different creation
 * identity is refused rather than silently returning a File that does not match
 * what was asked for.
 */
export const fileCreateSchema = z.object({
	fileName: z.string().min(1),
	contentType: z.string().min(1),
	isPublic: z.boolean().default(false),
	declaredFileSize: z.number().int().positive().max(FILE_MAX_SIZE_BYTES),
	idempotencyKey: z.uuid().optional(),
})

/**
 * Schema for presigning a single-PUT upload URL against an already-created File
 * (HTTP POST /files/{id}/upload-url body). `checksum` stays optional, matching
 * the legacy combined route's existing behavior — the browser flow always
 * supplies one, but the contract itself does not require it.
 */
export const requestUploadUrlSchema = z.object({
	checksum: z.string().optional(),
})

/**
 * Schema for creating/presigning/recovering a multipart upload session (HTTP
 * POST /files/{id}/multipart body). Shape-only: the identifier XOR and every
 * partNumbers rule (uniqueness, range, count, positivity) are enforced at the
 * service layer, not here — this repository's ValidationError path collapses
 * every zod rejection into a generic 422 with no stable `code`, which does not
 * satisfy the distinct 400 codes this contract requires. Do not "helpfully"
 * move those rules back into this schema.
 *
 * `classify` is deliberately NOT validated here at all — not even to the
 * extent of requiring an array — unlike `confirmUploadSchema`'s. This request
 * has three semantic branches (`multipartSessionId` re-presign, idempotent
 * same-attempt recovery, and genuinely new-session creation) and only the
 * last one may ever apply or reject a submitted `classify` — on the other two
 * it must be ignored outright, not rejected, REGARDLESS of its shape (an
 * object, a string, a number, `null`, or a malformed array all pass through
 * untouched). Constraining it to `z.array(...)` here — even without row-level
 * validation — would still reject a non-array `classify` on every branch,
 * including the two where the contract requires it to be silently ignored.
 * This field exists purely to transport the raw value to the branch-aware
 * service; row-level (and container-shape) validation for the new-session
 * branch lives in `multipartNewSessionClassifySchema` below, applied
 * explicitly by the service only on that branch. Do NOT "helpfully" add
 * `z.array(...)` or `classifyRowSchema` back here.
 */
export const multipartCreateSchema = z.object({
	uploadAttemptId: z.uuid().optional(),
	multipartSessionId: z.uuid().optional(),
	partNumbers: z.array(z.number()),
	classify: z.unknown().optional(),
})

/**
 * Row-level `classify` validation for a genuinely NEW multipart-session
 * creation only — see `multipartCreateSchema`'s doc for why the outer HTTP
 * shape schema cannot apply this itself. Identical to `confirmUploadSchema`'s
 * row shape (`classifyRowSchema`) — pinned equal by the schema-parity test in
 * file.schema.test.ts.
 */
export const multipartNewSessionClassifySchema = z.array(classifyRowSchema)

/**
 * Documentation-only mirror of `multipartCreateSchema` — never used to parse a
 * request. `multipartCreateSchema` accepts `classify` as `z.unknown()` so the
 * runtime can stay silent on shape for the two branches that ignore it
 * entirely; that permissiveness is correct at runtime but useless as a
 * published contract, since it tells a caller nothing about what to send.
 * This documents the caller-facing shape instead: what to send when creating
 * a new session, and when the field is (and isn't) honored.
 */
export const multipartCreateDocSchema = multipartCreateSchema.extend({
	classify: multipartNewSessionClassifySchema
		.optional()
		.describe(
			'Classification rows to snapshot onto the new session. Validated and stored ' +
				'only when this request creates a brand-new multipart session; ignored ' +
				'(any value, any shape) when re-presigning via multipartSessionId or ' +
				'recovering an in-flight same-attempt request.',
		),
})

/**
 * Schema for finalizing a multipart upload (HTTP POST
 * /files/{id}/multipart/complete body). The client sends the session it wants
 * finalized and nothing else: part numbers and ETags come from S3's own
 * `ListParts`, never from a caller (D1).
 */
export const multipartCompleteSchema = z.object({
	multipartSessionId: z.uuid(),
})

/**
 * Schema for confirming a file upload (HTTP POST /files/{id}/confirm-upload
 * body). Optional and additive — a caller sending no body at all (the
 * pre-existing behavior) is unaffected; `classify`, when present and
 * non-empty, is applied as a tracked `classification` concern rather than
 * requiring a separate `/classify/batch` call (see the `upload-processing-status`
 * capability). Flat rows, matching what `ClassificationService.classifyBatch`
 * consumes — NOT the grouped `classifyInputSchema` shape, which would reject
 * every submission the clients actually send.
 */
export const confirmUploadSchema = z.object({
	classify: z.array(classifyRowSchema).optional(),
})

/**
 * Schema for updating file metadata (HTTP PATCH /files/{id} body).
 *
 * `description` and `summary` are nullable so a caller can CLEAR them: absent skips
 * the field, a string sets it, and `null` empties it. Without the nullable, the only
 * way to empty a field would be `''`, which stores a second representation of
 * absence alongside NULL. The entity normalizes both to NULL on the way in.
 *
 * `extractionKey` is absent by design — it points at an artifact the extraction
 * pipeline produced, so it is not caller-settable.
 */
export const fileUpdateSchema = z.object({
	description: z.string().nullable().optional(),
	summary: z.string().nullable().optional(),
	entityType: z.string().optional(),
	entityId: z.uuid().optional(),
})

/**
 * Schema for creating a file share (HTTP POST /files/{id}/shares body).
 */
export const fileShareCreateSchema = z.object({
	userId: z.uuid(),
	permission: z.enum(FileSharePermission),
	expiresAt: z.coerce.date().optional(),
})

/**
 * Allow-list of `entityType` values accepted by the rebind endpoint.
 *
 * The rebind contract intentionally restricts the universe of valid targets
 * so a caller can't reassign files to arbitrary strings. Add new entries
 * here as new entity surfaces gain attachment support.
 */
export const REBINDABLE_ENTITY_TYPES = ['asset', 'deliverable'] as const
export type RebindableEntityType = (typeof REBINDABLE_ENTITY_TYPES)[number]

/**
 * Schema for the rebind endpoint (HTTP PATCH /files/rebind body).
 *
 * Rebinding moves a batch of files from a placeholder/legacy binding to a
 * real entity. Per-file ownership (uploader OR ADMIN/SUPER_ADMIN) is
 * enforced at the service layer — the schema only validates shape.
 */
export const fileRebindRequestSchema = z.object({
	fileIds: z.array(z.uuid()).min(1).max(100),
	target: z.object({
		entityType: z.enum(REBINDABLE_ENTITY_TYPES),
		entityId: z.uuid(),
	}),
})

/**
 * Schema for the file-discovery endpoint (HTTP POST /files/discover body).
 *
 * `filters` is a DIMENSION axis selection (axis code → value codes) resolved against
 * the classification core; an empty object means "the whole accessible library".
 */
export const fileDiscoverRequestSchema = z.object({
	filters: z.record(z.string(), z.array(z.string())).default({}),
	// object_attribute equality filter (free-text/list attributes like `order_code`,
	// `aspect_ratio`) — resolved separately from the dimension `filters` above.
	attributeFilters: z.record(z.string(), z.array(z.string())).default({}),
	page: z.coerce.number().int().positive().optional(),
	limit: z.coerce.number().int().positive().max(100).optional(),
	/**
	 * How `filters` is matched against a file's tags.
	 *
	 * `filed_under` (the default, and what every existing caller gets) returns files tagged AT or
	 * BELOW a filter value — the Library's browse.
	 *
	 * `applies_in` returns files whose tag COVERS the context: a file tagged `emea` applies in
	 * Spain, one tagged `emea_fr` does not. A market-scoped view needs this, and cannot get it by
	 * widening a browse filter — asking browse for Spain via `[emea_es, emea, global]` matches the
	 * whole library, because every market descends from `global`.
	 */
	match: z.enum(['filed_under', 'applies_in']).default('filed_under'),
})

export type FileDiscoverRequestInput = z.infer<typeof fileDiscoverRequestSchema>
export type FileUploadRequestInput = z.infer<typeof fileUploadRequestSchema>
export type FileCreateInput = z.infer<typeof fileCreateSchema>
export type RequestUploadUrlInput = z.infer<typeof requestUploadUrlSchema>
export type MultipartCreateInput = z.infer<typeof multipartCreateSchema>
export type MultipartCompleteInput = z.infer<typeof multipartCompleteSchema>
export type ConfirmUploadInput = z.infer<typeof confirmUploadSchema>
export type FileUpdateInput = z.infer<typeof fileUpdateSchema>
export type FileShareCreateInput = z.infer<typeof fileShareCreateSchema>
export type FileRebindRequestInput = z.infer<typeof fileRebindRequestSchema>
